import React, { useState, useEffect } from "react";
import Axios from "axios";
import moment from "moment-timezone";

import { useFormatter } from "./../../util/formatter";
import { useSite } from "./../../util/sites";
import GYAxios from "../../util/GYAxios";

import DashboardGraphs from "./DashboardGraphs";
import DashboardMain from "./DashboardMain";
import DashboardPeriodData from "./DashboardPeriodData";
import LoaderGraph from "../general/LoaderGraph";

function DashboardContent() {
	const currentSite = useSite();
	const [selectedSite, setSelectedSite] = useState(null);
	const [period, setPeriod] = useState(null);
	const [data, setData] = useState({});
	const [mainData, setMainData] = useState(null);
	const [graphData, setGraphData] = useState(null);
	const [monthlyData, setMonthlyData] = useState(null);
	const [show, setShow] = useState("");
	const [selectedDuration, setSelectedDuration] = useState(null);
	const [sensorNumbers, setSensorNumbers] = useState(null);
	const [cctvIRAlarmTempData, setCctvIRAlarmTempData] = useState(null);
	const [formatNumber, isEmpty] = useFormatter();

	/**
	 * Sum all elements in array
	 * @param {Array to be calculated} arr
	 */
	const valueSum = (arr) => {
		return arr.reduce((a, b) => a + b);
	};

	/**
	 * Make an average of the elements in array
	 * @param {Array to be calculated} arr
	 */
	const valueAvg = (arr) => {
		const size = arr.length;
		return valueSum(arr) / size;
	};

	/**
	 * Set the fromDate and toDate to filter based on selected site and duration
	 * @param {Selected Site} selectedSite
	 * @param {Selected duration from period section} selectedDuration
	 */
	const prepareDetailsRequest = (selectedSite, selectedDuration) => {
		if (selectedDuration != null) {
			const cod = moment(selectedSite.cod);
			const currentDate = moment();

			let fromDate = "";
			let toDate = "";
			let ref_date = "";

			switch (selectedDuration) {
				case "THIS_MONTH":
					ref_date = currentDate;
					toDate = moment().format("YYYY-MM-DD");
					break;

				case "LAST_MONTH":
					ref_date = currentDate.subtract(1, "months");
					toDate = ref_date.endOf("month").format("YYYY-MM-DD");
					break;

				case "THIS_YEAR":
					ref_date = currentDate.startOf("year");
					toDate = moment().format("YYYY-MM-DD");
					break;

				default:
					break;
			}

			if (
				cod.format("MM") >= ref_date.format("MM") &&
				cod.format("YY") >= ref_date.format("YY")
			) {
				fromDate = cod.format("YYYY-MM-DD");
			} else {
				fromDate = ref_date.startOf("month").format("YYYY-MM-DD");
			}

			return {
				fromDate,
				toDate,
			};
		} else return null;
	};

	/**
	 * Filter data based on period selected
	 * @param {Monthly Data array} monthlyDataArr
	 * @param {Reference period to filter data} period
	 */
	const filterDurationData = (monthlyDataArr, period) => {
		const fromDate = period.fromDate;
		const toDate = period.toDate;
		const monthlyDataTemp =
			monthlyDataArr != null
				? monthlyDataArr.map((data) => {
						const rowDate = moment(data.dateData);

						// [] - inclusive of fromDate and toDate
						// () - exclusive of fromDate and toDate
						// [) - inclusive of fromDate but exclusive of toDate
						if (rowDate.isBetween(fromDate, toDate, undefined, "[]")) {
							return {
								...data,
								dateData: rowDate.format("YYYY-MM-DD"),
							};
						}
				  })
				: null;

		const filtered =
			monthlyDataTemp != null
				? monthlyDataTemp.filter((row) => typeof row != "undefined")
				: null;
		return filtered;
	};

	/**
	 * Prepare data for period select bar
	 * @param {Monthly Data array} monthlyData
	 * @param {Reference period to filter data} period
	 */
	const getDetails_UND = (monthlyData, period) => {
		const filteredMonthlyData = filterDurationData(monthlyData, period);

		let tempObj = {};
		if (filteredMonthlyData != null && filteredMonthlyData.length > 0) {
			if (filteredMonthlyData.length > 1) {
				let solarArr = [];
				let solarONArr = [];
				let solarOFFArr = [];
				let gridArr = [];
				let gridONArr = [];
				let gridOFFArr = [];
				let irradArr = [];
				let price_onArr = [];
				let price_offArr = [];
				let price_FTArr = [];
				let discount_onArr = [];
				let discount_offArr = [];
				let discount_FTArr = [];
				let capacityArr = [];
				let brut_PRArr = [];

				filteredMonthlyData.map((row) => {
					solarArr.push(row.solar_total);
					solarONArr.push(row.solar_ON);
					solarOFFArr.push(row.solar_OFF);
					gridArr.push(row.grid_total);
					gridONArr.push(row.grid_ON);
					gridOFFArr.push(row.grid_OFF);
					irradArr.push(row.irradiance_sum);
					price_onArr.push(row.price_on);
					price_offArr.push(row.price_off);
					price_FTArr.push(row.FT_cost);
					discount_onArr.push(row.ON_discount);
					discount_offArr.push(row.OFF_discount);
					discount_FTArr.push(row.FT_discount);
					capacityArr.push(row.power);
					brut_PRArr.push(row.brut_PR);
				});
				tempObj = {
					solar_total: valueSum(solarArr),
					solar_ON: valueSum(solarONArr),
					solar_OFF: valueSum(solarOFFArr),
					grid_total: valueSum(gridArr),
					grid_ON: valueSum(gridONArr),
					grid_OFF: valueSum(gridOFFArr),
					irradiance_sum: valueSum(irradArr),
					price_on: price_onArr[0],
					price_off: price_offArr[0],
					FT_cost: price_FTArr[0],
					currency: filteredMonthlyData[0].currency,
					ON_discount: discount_onArr[0],
					OFF_discount: discount_offArr[0],
					FT_discount: discount_FTArr[0],
					power: capacityArr[0],
					brut_PR: valueAvg(brut_PRArr),
				};
			} else tempObj = filteredMonthlyData[0];
		} else {
			tempObj = {
				solar_total: 0,
				solar_ON: 0,
				solar_OFF: 0,
				grid_total: 0,
				grid_ON: 0,
				grid_OFF: 0,
				irradiance_sum: 0,
				price_on: 0,
				price_off: 0,
				price_FT: 0,
				ON_discount: 0,
				OFF_discount: 0,
				FT_discount: 0,
				power: 0,
				brut_PR: 0,
			};
		}

		const dataObj = {
			solar: tempObj.solar_total,
			solarON: tempObj.solar_ON,
			solarOFF: tempObj.solar_OFF,
			grid: tempObj.grid_total,
			gridON: tempObj.grid_ON,
			gridOFF: tempObj.grid_OFF,
			irrad: tempObj.irradiance_sum,
			price_on: tempObj.price_on,
			price_off: tempObj.price_off,
			price_FT: tempObj.FT_cost,
			discount_on: tempObj.ON_discount,
			discount_off: tempObj.OFF_discount,
			discount_FT: tempObj.FT_discount,
			capacity: tempObj.power,
			brut_PR: tempObj.brut_PR,
			currency: tempObj.currency,
		};

		setData(dataObj);
	};

	/**
	 * Prepare data for main dashboard of selected site
	 * @param {Selected site} selectedSite
	 */
	const getDetails_MDS = (selectedSite) => {
		let fullData = {
			todayData: {
				ambient_temp: 0,
				gridTotal: 0,
				gridON: 0,
				gridOFF: 0,
				grid_power: 0,
				irr: 0,
				module_temp: 0,
				solarTotal: 0,
				solarON: 0,
				solarOFF: 0,
				solar_power: 0,
			},
			yesterdayData: {
				ambient_temp: 0,
				gridTotal: 0,
				gridON: 0,
				gridOFF: 0,
				grid_power: 0,
				irr: 0,
				module_temp: 0,
				solarTotal: 0,
				solarON: 0,
				solarOFF: 0,
				solar_power: 0,
			},
			senSavData: {
				module_temp: 0,
				ambient_temp: 0,
				irradiance: 0,
				grand_total_production: 0,
				grid_total: 0,
			},
		};

		if (selectedSite != null) {
			// Param for API Requests
			const tableName = selectedSite.table_name;
			const siteID = selectedSite.site_id;
			const cod = selectedSite.cod;
			const dateFormat = "YYYYMMDD";

			const toDate = moment().format(dateFormat);

			// Daily Data Request
			const dailyFromDate = moment().subtract(1, "day").format(dateFormat);

			const dailyDataURI = `/daily/site/${siteID}/period/${dailyFromDate}/${toDate}/order/dateData/DESC`;
			const reqEnergyDaily = GYAxios.get(dailyDataURI);

			// Live Data Request
			const reqLiveURI = `/p10/single_row/site/${tableName}/date/${toDate}/END`;
			const reqLive = GYAxios.get(reqLiveURI);

			// Monthly Data Request
			const monthlyFromDate = moment(cod).format(dateFormat);

			const reqMonthlyURI = `/invoice/site/${siteID}/period/${monthlyFromDate}/${toDate}`;
			const reqEnergyMonthly = GYAxios.get(reqMonthlyURI);

			const reqCCTVIRTempAlarmURI = `/alarms/cctv_ir_temp_alarms?table_name=${tableName}`;
			const reqCCTVIRTempAlarm = GYAxios.get(reqCCTVIRTempAlarmURI);

			// Making API requests
			Axios.all([reqEnergyDaily, reqLive, reqEnergyMonthly, reqCCTVIRTempAlarm])
				.then(
					Axios.spread((...responses) => {
						const resDataDaily = responses[0];
						const resLive = responses[1];
						const hasLiveData = resLive.data != null && resLive.data.length > 0;
						const resDataMonthly =
							responses[2].data.error == null ? responses[2].data : [];
						
						const cctvIRTempAlarm = responses[3].data?.[tableName] || null;
						setCctvIRAlarmTempData(cctvIRTempAlarm);

						let totalSolarProd = 0;

						setMonthlyData(resDataMonthly);

						if (resDataMonthly != null) {
							resDataMonthly.map((row) => (totalSolarProd += row.solar_total));
						}

						// Today's data
						fullData.todayData = {
							ambient_temp: resDataDaily.data[0].avg_temp_ambient,
							gridTotal: resDataDaily.data[0].grid_total,
							gridON: resDataDaily.data[0].grid_ON,
							gridOFF: resDataDaily.data[0].grid_OFF,
							grid_power: hasLiveData ? resLive.data[0].grid_power : 0,
							irr: hasLiveData ? resLive.data[0].irradiance : 0,

							module_temp: hasLiveData ? resLive.data[0].module_temp : 0,
							solarTotal: resDataDaily.data[0].solar_total,
							solarON: resDataDaily.data[0].solar_ON,
							solarOFF: resDataDaily.data[0].solar_OFF,
							solar_power: hasLiveData ? resLive.data[0].solar_power : 0,
						};

						// Sensors and Savings data
						fullData.senSavData = hasLiveData
							? { ...resLive.data[0], totalSolarProd }
							: { totalSolarProd };

						// Sites has yesterday's data
						if (resDataDaily.data.length > 1) {
							fullData.yesterdayData = {
								ambient_temp: resDataDaily.data[1].avg_temp_ambient,
								gridTotal: resDataDaily.data[1].grid_total,
								gridON: resDataDaily.data[1].grid_ON,
								gridOFF: resDataDaily.data[1].grid_OFF,
								grid_power: hasLiveData ? resLive.data[0].grid_power : 0,
								irr: hasLiveData ? resLive.data[0].irradiance : 0,

								module_temp: hasLiveData ? resLive.data[0].module_temp : 0,
								solarTotal: resDataDaily.data[1].solar_total,
								solarON: resDataDaily.data[1].solar_ON,
								solarOFF: resDataDaily.data[1].solar_OFF,
								solar_power: hasLiveData ? resLive.data[0].solar_power : 0,
							};
						}

						setMainData(fullData);
					})
				)
				.catch((err) => console.log("Multiple equest error :", err));
		}
	};

	/**
	 * Format sensor object
	 * @param {Sensor object} obj
	 * @param {P10 data} p10Data
	 */
	const formatSensorsData = (obj, p10Data) => {
		const keys = Object.keys(obj);
		keys.map((key) => {
			obj[key].push(p10Data[key]);
		});
		return obj;
	};

	/**
	 * Format sensors array
	 * @param {Sensors} sensor
	 * @param {Number of sensors} number
	 */
	const formatSensorObj = (sensor, number) => {
		let tempObj = {};

		if (number === 1) tempObj[`${sensor}`] = [];
		else {
			if (sensor === "irradiance") tempObj[`${sensor}`] = [];
			for (let i = 0; i < number; i++) {
				const colName = `${sensor}_${i + 1}`;
				tempObj[colName] = [];
			}
		}
		return tempObj;
	};

	/**
	 * Prepare data for graphs section
	 * @param {Selected Site} selectedSite
	 */
	const getDetails_MGS = (selectedSite) => {
		const today = moment().format("YYYY-MM-DD");

		const hasWindSensors =
			selectedSite.wind_speed != null && selectedSite.wind_speed > 0;
		const hasWaterSensors =
			selectedSite.water != null && selectedSite.water > 0;

		const period = {
			fromDate: today,
			toDate: today,
		};

		let graphDataObj = {
			dates: [],
			solar_power: [],
		};

		const tableName = selectedSite.table_name;
		const fromDate = period.fromDate;
		const toDate = period.toDate;

		const p10URI = `/p10/site/${tableName}/period/${fromDate}/${toDate}`;
		GYAxios.get(p10URI)
			.then((res) => {
				if (res.data.res_length != 0) {
					const ambNo =
						selectedSite.nb_ambient != null ? selectedSite.nb_ambient : 0;
					const modNo =
						selectedSite.nb_module != null ? selectedSite.nb_module : 0;
					const irrNo =
						selectedSite.nb_irr_sensors != null
							? selectedSite.nb_irr_sensors
							: 0;
					const windNo =
						selectedSite.wind_speed != null ? selectedSite.wind_speed : 0;
					const waterNo = selectedSite.water != null ? selectedSite.water : 0;

					setSensorNumbers({ ambNo, modNo, irrNo, windNo, waterNo });

					let ambObj = formatSensorObj("ambient_temp", ambNo);
					let modObj = formatSensorObj("module_temp", modNo);
					let irrObj = formatSensorObj("irradiance", irrNo);

					let windObj = hasWindSensors
						? formatSensorObj("wind_speed", windNo)
						: null;

					let waterObj = hasWaterSensors
						? formatSensorObj("water_level", waterNo)
						: null;

					res.data.map((row) => {
						graphDataObj.dates.push(moment(row.dateData).format("MM/DD HH:mm"));
						graphDataObj.solar_power.push(row.solar_power);

						formatSensorsData(ambObj, row);
						formatSensorsData(modObj, row);
						formatSensorsData(irrObj, row);
						if (windObj != null) formatSensorsData(windObj, row);
						if (waterObj != null) formatSensorsData(waterObj, row);
					});

					graphDataObj = {
						...graphDataObj,
						...ambObj,
						...modObj,
						...irrObj,
						...windObj,
						...waterObj,
					};
				}

				setGraphData(graphDataObj);
			})
			.catch((err) => console.log("Request error - /p10:", err));
	};

	useEffect(() => {
		if (
			selectedSite == null ||
			(selectedSite != null &&
				currentSite.selectedSite != null &&
				currentSite.selectedSite.value.table_name !== selectedSite.table_name)
		) {
			setSelectedSite(currentSite.selectedSite.value);
		}
	}, [currentSite.selectedSite]);

	useEffect(() => {
		setMainData(null);

		if (selectedSite != null) {
			getDetails_MDS(selectedSite);
			getDetails_MGS(selectedSite);
		}
	}, [selectedSite]);

	useEffect(() => {
		if (selectedSite != null && selectedDuration != null) {
			const periodObj = prepareDetailsRequest(selectedSite, selectedDuration);
			setPeriod(periodObj);
		}
	}, [selectedSite, selectedDuration]);

	useEffect(() => {
		if (period != null) getDetails_UND(monthlyData, period);
	}, [period, monthlyData]);

	return (
		selectedSite != null && (
			<>
				<DashboardPeriodData
					selectedSite={selectedSite}
					setSelectedDuration={setSelectedDuration}
					data={data}
					setShow={setShow}
				/>
				<div id="selectedSiteMain" className={show}>
					{mainData != null ? (
						<DashboardMain
							isEmpty={isEmpty}
							selectedSite={selectedSite}
							data={mainData}
							cctvIRAlarmTempData={cctvIRAlarmTempData}
						/>
					) : (
						<LoaderGraph style={{ height: "450px", padding: "20px 0px" }} />
					)}
					{graphData != null ? (
						<DashboardGraphs
							selectedSite={selectedSite.site_name}
							sensorNumbers={sensorNumbers}
							data={graphData}
						/>
					) : (
						<LoaderGraph style={{ height: "450px", padding: "20px 0px" }} />
					)}
				</div>
			</>
		)
	);
}

export default DashboardContent;
